import * as React from 'react';
import {useDispatch} from 'react-redux';
import {Link as RoutedLink} from 'react-router-dom';

import {BillingSummary} from 'shells/billing/billing-summary/billing-summary';
import {EnhanceYourPlan} from 'shells/billing/billing-summary/enhance-your-plan';
import {UsageScorecard} from 'shells/billing/usage-scorecard/usage-scorecard';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {analyticsClient} from '../../../analytics-client';

import {useGetInstanceInfo} from '../../core/use-get-instance-info';
import {PageContainer, PageTitleBar} from '../../layout';
import {OpenableModals} from '../../openable-modals/openable-modals';
import * as actions from '../../openable-modals/openable-modals-actions';

import {BillingInfo} from './billing-info';
import {CancelledPlanBanner} from './cancelled-plan-banner';
import {
    useGetAccountBilling,
    useGetBillingInfo,
    useGetAvailablePlans,
    useGetUsageScorecard,
} from './graphql/hooks';
import {getAddOnsNotIncludedInPlan} from './helpers';
import {removeRedundantAddOns} from './manage/helpers';
import {McfxSubscriptionInfo} from './mcfx-subscription-info';
import {NextEstimatedInvoice} from './next-estimated-invoice';
import {SupportSection} from './pause-cancel-flow/support-section';
import {SpecializedBillingInfo} from './specialized-billing-info';
import {TrialSummaryPage} from './trial-summary-page/trial-summary-page';

import styles from './home.module.css';

const LARGE_TEAM_MESSAGE_MIN_QTY = 10;

export function Home() {
    const [isViewingBillingForm, setIsViewingBillingForm] = React.useState<boolean>(false);

    const isBillingManagedByWebfx = NutClientConfig.isWebfx;
    const isBillingSpecializedParent = NutClientConfig.isBillingSpecializedParent;
    const isBillingSpecializedChild = NutClientConfig.isBillingSpecializedChild;
    const isBillingSpecialized = isBillingSpecializedParent || isBillingSpecializedChild;

    const dispatch = useDispatch();
    const {
        accountBilling,
        isLoading,
        errorMessage: accountBillingErrorMessage,
    } = useGetAccountBilling(isBillingManagedByWebfx);
    const {
        addOns,
        isLoading: isLoadingAvailablePlans,
        errorMessage: availablePlansErrorMessage,
    } = useGetAvailablePlans(isBillingManagedByWebfx);
    const {
        billingInfo,
        refetch: refetchBillingInfo,
        isLoading: isLoadingBillingInfo,
        errorMessage: billingInfoErrorMessage,
    } = useGetBillingInfo(isBillingManagedByWebfx);

    const {sessionInstance, isLoading: isLoadingInstanceInfo} = useGetInstanceInfo();
    const {usageScorecard, isLoading: isLoadingUsageScorecard} = useGetUsageScorecard();
    const hasDeprecatedBillingCycle = sessionInstance && sessionInstance.hasDeprecatedBillingCycle;
    const isTrial = sessionInstance && sessionInstance.isTrial;

    const shouldDisplayLargeTeamMessage =
        accountBilling && accountBilling.currentPlan && accountBilling.currentPlan.crm
            ? accountBilling.currentPlan.crm.quantity >= LARGE_TEAM_MESSAGE_MIN_QTY
            : false;

    const currentPlan = accountBilling && accountBilling.currentPlan;

    const relevantAddOns = removeRedundantAddOns(
        addOns,
        accountBilling &&
            accountBilling.currentPlan &&
            accountBilling.currentPlan.marketingSuite &&
            accountBilling.currentPlan.marketingSuite.planCode,
        accountBilling &&
            accountBilling.currentPlan &&
            accountBilling.currentPlan.inbox &&
            accountBilling.currentPlan.inbox.planCode
    );

    const addOnsNotIncludedInPlan = getAddOnsNotIncludedInPlan(
        relevantAddOns,
        accountBilling && accountBilling.currentPlan
    );

    const handleOpenModal = () => {
        dispatch(actions.openModal(OpenableModals.CAMPAIGNS_ONBOARDING_MODAL));
    };

    React.useEffect(() => {
        if (isTrial) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_CUSTOMER_TRIAL_VISITS_BILLING_HOME);
        }
    }, [isTrial]);

    React.useEffect(() => {
        if (!isLoadingBillingInfo && !billingInfo) {
            setIsViewingBillingForm(true);
        }
    }, [billingInfo, isLoadingBillingInfo]);

    return (
        <PageContainer
            title='Billing'
            pageTitleBar={
                <PageTitleBar
                    title='Billing'
                    subheaderPrimaryText='Update your Nutshell plan, change your payment method, and download invoices.'
                    buttons={
                        !sessionInstance || isBillingManagedByWebfx || isBillingSpecialized
                            ? undefined
                            : [
                                  <Link
                                      key='update-my-plan'
                                      // @ts-expect-error
                                      as={RoutedLink}
                                      variant='button-primary'
                                      size='big'
                                      to='/setup/billing/sales'
                                  >
                                      Update my plan
                                  </Link>,
                              ]
                    }
                />
            }
            whiteBackground={true}
        >
            {isLoadingInstanceInfo && <LoadingPage size={48} />}
            {hasDeprecatedBillingCycle && (
                <NothingFoundEmptyState
                    title='Your plan is on a billing cycle that is no longer supported'
                    subtitle={
                        <>
                            Please reach out to{' '}
                            <Link variant='primary' href='mailto:support@nutshell.com'>
                                support@nutshell.com
                            </Link>{' '}
                            to change your billing plan.
                        </>
                    }
                />
            )}
            {!isLoadingInstanceInfo && isBillingManagedByWebfx && <McfxSubscriptionInfo />}
            {!isLoadingInstanceInfo && isBillingSpecialized && (
                <SpecializedBillingInfo isParent={isBillingSpecializedParent} />
            )}
            {!isLoadingInstanceInfo && isTrial && sessionInstance && (
                <TrialSummaryPage daysLeftInTrial={sessionInstance.daysLeftInTrial ?? 0} />
            )}
            {!hasDeprecatedBillingCycle &&
                !isBillingManagedByWebfx &&
                !isTrial &&
                !isLoadingInstanceInfo &&
                !isBillingSpecialized && (
                    <>
                        {currentPlan && currentPlan.isCancelled && <CancelledPlanBanner />}
                        <div className={styles.container}>
                            <StackLegacy spacing={24}>
                                <BillingSummary
                                    plan={currentPlan}
                                    isLoading={isLoading}
                                    includeHeaderLinks={true}
                                    errorMessage={accountBillingErrorMessage}
                                    // @ts-expect-error
                                    hideColumns={true}
                                />
                                {!accountBillingErrorMessage &&
                                    addOnsNotIncludedInPlan.length !== 0 && (
                                        <EnhanceYourPlan
                                            addOns={addOnsNotIncludedInPlan}
                                            onOpenModal={handleOpenModal}
                                            isLoading={isLoading || isLoadingAvailablePlans}
                                            errorMessage={availablePlansErrorMessage}
                                        />
                                    )}
                                {!accountBillingErrorMessage && usageScorecard && (
                                    <UsageScorecard
                                        sections={usageScorecard.sections}
                                        isLoading={isLoadingUsageScorecard}
                                    />
                                )}
                            </StackLegacy>
                            <StackLegacy spacing={24}>
                                {!accountBillingErrorMessage && (
                                    <NextEstimatedInvoice
                                        nextEstimatedInvoice={
                                            currentPlan && currentPlan.nextEstimatedInvoice
                                        }
                                        lastThreeInvoices={
                                            accountBilling && accountBilling.lastThreeInvoices
                                        }
                                        isLoading={isLoading}
                                    />
                                )}
                                <BillingInfo
                                    billingInfo={billingInfo}
                                    refetchBillingInfo={refetchBillingInfo}
                                    isLoading={isLoadingBillingInfo}
                                    errorMessage={billingInfoErrorMessage}
                                    isViewingBillingForm={isViewingBillingForm}
                                    setIsViewingBillingForm={setIsViewingBillingForm}
                                />
                                <SupportSection
                                    displayLargeTeamMessage={shouldDisplayLargeTeamMessage}
                                    isNewSubscription={Boolean(
                                        billingInfo && billingInfo.isNewSubscription
                                    )}
                                />
                            </StackLegacy>
                        </div>
                    </>
                )}
        </PageContainer>
    );
}
